import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { AccountModal } from './AccountModal';
import { LockScreen } from './LockScreen';
import { PaymentRetryingBanner } from './PaymentRetryingBanner';
import { useAuthStore } from '../stores/authStore';
import { isLocked } from '../lib/workspaces';
import {
  FOUNDER_CALL_URL,
  dismissFounderCall,
  isFounderCallDismissed,
  loadFounderCallEmbed,
  openFounderCall,
} from '../lib/founderCall';

const hasValidAvatar = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (url.includes('supabase') && url.includes('/storage/')) return true;
  if (url.includes('googleusercontent.com')) return true;
  if (url.includes('githubusercontent.com')) return true;
  if (url.includes('platform-lookaside.fbsbx.com')) return true;
  if (url.includes('pbs.twimg.com')) return true;
  return false;
};

export function AppLayout() {
  const { user, profile, workspace, loading, fetchProfile } = useAuthStore();
  const location = useLocation();
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [dismissedFounderCallIds, setDismissedFounderCallIds] = useState<string[]>([]);
  const isFounderCallHidden =
    !!workspace?.id && (dismissedFounderCallIds.includes(workspace.id) || isFounderCallDismissed(workspace.id));

  // The workspace state changes without the app doing anything: the Trial runs out, or a
  // Subscription is paid or ends elsewhere. Reload it when the Trial ends and whenever the
  // tab comes back into focus, so the app never shows a state the database has moved past.
  const queryClient = useQueryClient();
  const userId = user?.id;
  const trialEndsAt = workspace?.state === 'trial' ? workspace.trial_ends_at : null;

  useEffect(() => {
    if (!userId) return;
    const reload = () => {
      if (document.visibilityState === 'visible') fetchProfile(userId).catch(console.error);
    };
    document.addEventListener('visibilitychange', reload);
    return () => document.removeEventListener('visibilitychange', reload);
  }, [userId, fetchProfile]);

  useEffect(() => {
    if (!userId || !trialEndsAt) return;
    // A second late, so the database agrees the Trial has ended. Capped at setTimeout's
    // limit (~24 days); an earlier reload just schedules this again.
    const delay = Math.min(Date.parse(trialEndsAt) - Date.now() + 1000, 2 ** 31 - 1);
    const timer = setTimeout(() => fetchProfile(userId).catch(console.error), Math.max(delay, 0));
    return () => clearTimeout(timer);
  }, [userId, trialEndsAt, fetchProfile]);

  // Cached page data was read under the old state: real results once Locked, or empty
  // ones once paid for again. Refetch it all when the same workspace changes state.
  const lastState = useRef<{ id?: string; state?: string }>({});
  useEffect(() => {
    const previous = lastState.current;
    lastState.current = { id: workspace?.id, state: workspace?.state };
    if (previous.id && previous.id === workspace?.id && previous.state !== workspace?.state) {
      queryClient.invalidateQueries();
    }
  }, [workspace?.id, workspace?.state, queryClient]);

  useEffect(() => {
    loadFounderCallEmbed().catch(console.error);
  }, []);

  useEffect(() => {
    const workspaceName = workspace?.name?.trim() || 'Workspace';

    const routeTitleMap: Record<string, string> = {
      '/overview': 'Overview',
      '/analytics': 'Overview',
      '/prompts': 'Prompts',
      '/competitors': 'Competitors',
      '/mentions': 'Mentions',
      '/searches': 'Searches',
      '/insights': 'Insights',
      '/audit': 'AI Site Audit',
      '/ai-audit': 'AI Site Audit',
      '/ai-analysis': 'AI Site Audit',
      '/plans': 'Plans',
    };

    const pageTitle = routeTitleMap[location.pathname] || 'Workspace';
    const title = `Lima: ${pageTitle} | ${workspaceName}`;

    document.title = title;

    const ogTitle = document.querySelector('meta[property="og:title"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');

    ogTitle?.setAttribute('content', title);
    twitterTitle?.setAttribute('content', title);
  }, [location.pathname, workspace?.name]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  // Redirects are keyed on the location so they fire again if something else navigates
  // back here before they settle (e.g. switching workspace from the sidebar).
  // An Awaiting payment workspace is paid for in onboarding before anything else.
  if (workspace?.state === 'awaiting_payment') {
    return <Navigate key={location.key} to="/onboarding/payment" replace />;
  }

  if (profile && (!profile.onboarding_completed || !profile.workspace_id)) {
    // Workspaces created with their brand domain pick up onboarding after it.
    return <Navigate key={location.key} to={workspace?.brand_url ? '/onboarding/step-2' : '/onboarding/step-1'} replace />;
  }

  const getInitial = () => {
    if (profile?.first_name) return profile.first_name[0].toUpperCase();
    return 'U';
  };

  const getFullName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return 'User';
  };

  const showAvatar = hasValidAvatar(profile?.avatar_url);

  return (
    <div className="app-wrapper">
      <Sidebar
        showFounderCallLink={isFounderCallHidden}
        onOpenAccount={() => setIsAccountModalOpen(true)}
      />
      <main className="section-application">
        <div className="right-nav">
          <div
            className="user-nav-panel"
            onClick={() => setIsAccountModalOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            <div className="text-size-small text-weight-medium text-colour-dark-grey">
              {getFullName()}
            </div>
            <div className="user-avatar">
              {showAvatar && profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="avatar-image" />
              ) : (
                <div className="initial">{getInitial()}</div>
              )}
            </div>
          </div>
        </div>
        <div className="container-full">
          {workspace && <PaymentRetryingBanner workspace={workspace} />}
          {workspace && !isFounderCallHidden && (
            <div className="trial-ended-banner is-founder-call">
              <div className="trial-ended-banner-copy-text">
                <div className="text-size-regular text-weight-semibold">Speak with our founder</div>
                <div className="text-size-small text-colour-alt-grey">
                  Want help getting the most out of Lima? Book a free personal walkthrough.
                </div>
              </div>
              <a
                href={FOUNDER_CALL_URL}
                className="button is-small w-button"
                onClick={(event) => {
                  event.preventDefault();
                  openFounderCall();
                }}
              >
                Book a 20-minute call
              </a>
              <button
                type="button"
                className="founder-call-dismiss"
                aria-label="Dismiss"
                onClick={() => {
                  setDismissedFounderCallIds((ids) => [...ids, workspace.id]);
                  dismissFounderCall(workspace.id);
                }}
              >
                <X size={14} />
              </button>
            </div>
          )}
          {workspace && isLocked(workspace) ? <LockScreen workspace={workspace}><Outlet /></LockScreen> : <Outlet />}
        </div>
      </main>

      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
      />

    </div>
  );
}
